import React, { useState } from 'react';
import { ScanResultDto } from '../types/api';
import {
  parseIndicators, gateOf, rsiTone, rsiStateLabel, emaSpreadPct, fmtINR,
} from '../utils/signal';
import { RsiGauge, Gate, SignalPill, Sparkline, signalColor } from './Indicators';
import { StockDetail } from './StockDetail';

/**
 * RSI-hero tile.
 *
 * Leads with RSI (the first read in the strategy) as the dominant element with an
 * oversold↔overbought gauge, then exposes the RSI → ADX → EMA gate so you can see
 * WHY the stock is in its bucket. Price + sparkline sit to the side. Click to open
 * the full breakdown drawer.
 */
export function StockRow({ result, rank }: { result: ScanResultDto; rank?: number }) {
  const [open, setOpen] = useState(false);
  const cls = result.classification ?? 'NONE';
  const { rsi } = parseIndicators(result);
  const gate = gateOf(result);
  const spread = emaSpreadPct(result);
  const color = signalColor(cls);

  return (
    <>
      <button
        type="button"
        className={`stock-card signal-${cls.toLowerCase()}`}
        onClick={() => setOpen(true)}
        aria-label={`${result.symbol} details`}
      >
        <span className="tile-acc" />

        <div className="tile-head">
          <div className="tile-id">
            {rank != null && <span className="tile-rank">#{rank}</span>}
            <span className="ticker">{result.symbol}</span>
          </div>
          <SignalPill classification={cls} score={result.score} />
        </div>

        <div className="rsi-hero">
          <div className="rsi-num-wrap">
            <span className="rsi-label">RSI</span>
            <span className={`rsi-num ${rsiTone(rsi)}`}>{rsi != null ? rsi.toFixed(1) : '—'}</span>
            <span className="rsi-state">{rsiStateLabel(rsi)}</span>
          </div>
          <div className="rsi-right">
            <span className="tile-price">{fmtINR(result.currentPrice)}</span>
            <div className="tile-spark">
              <Sparkline values={result.priceHistory} color={color} height={36} />
            </div>
          </div>
        </div>

        <RsiGauge value={rsi} />

        <div className="tile-foot">
          <Gate gate={gate} size="sm" />
          <div className="tile-micro">
            <span>ADX <b>{parseIndicators(result).adx?.toFixed(1) ?? '—'}</b></span>
            <span className={spread != null ? (spread >= 0 ? 'up' : 'dn') : ''}>
              EMA <b>{spread != null ? `${spread >= 0 ? '+' : ''}${spread.toFixed(1)}%` : '—'}</b>
            </span>
          </div>
        </div>
      </button>

      {open && <StockDetail result={result} onClose={() => setOpen(false)} />}
    </>
  );
}
